/* yacht-fleet-drop.js — Step One · Two-Chapter Product Drop + Bundle Builder
 *
 * Generic, reusable component: Chapter 1 + optional Chapter 2 (hideable) + a Bundle
 * Builder that groups Chapter 1's products into two merchant-labelled buckets ("Group
 * A" / "Group B" internally — e.g. Boxer Brief / Trunk for Yacht Rocks) plus a Custom
 * Mix. First built for Yacht Rocks + Capri Yellow; every product-specific label now
 * comes from section settings rather than being hardcoded here, so the same file works
 * for a men's-only page, a women's-only page, or any future two-chapter drop.
 *
 * Responsibilities:
 *   1. Smooth-scroll anchor nav (same as story-drop.js)
 *   2. GA4 merchandising-attribution instrumentation, following the pattern in
 *      GA4-MERCHANDISING-TRACKING-HANDOFF.md:
 *        - Chapter 1 grid  -> ee_view_item_list / ee_select_item
 *        - Chapter 2 grid  -> ee_view_item_list / ee_select_item (skipped entirely if
 *          Chapter 2 is hidden — the grid element simply isn't in the DOM)
 *        - Bundle builder  -> ee_view_promotion / ee_select_promotion
 *      IMPORTANT: this file NEVER pushes add_to_cart / remove_from_cart / begin_checkout /
 *      purchase — Analyzify's sandboxed pixel already emits those from the real
 *      /cart/add.js call. Duplicating them double-counts revenue.
 *   3. Bundle builder logic (quick-pick tabs, size resolution, live tier progress
 *      projected on cart subtotal + selection — the same number the checkout
 *      Function discount evaluates — kept fresh via the cart:change broadcast,
 *      add-to-cart via the standard _s1.addToCartAndRefreshCart pattern).
 *
 * Scoped entirely to elements inside .yfd-section so it can coexist with the
 * unrelated story-drop.js on the same page.
 */
(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────────────
   * 0. Smooth-scroll anchor nav
   * ──────────────────────────────────────────────────────────────── */
  document.querySelectorAll('a[href^="#yfd-"]').forEach(function (a) {
    a.addEventListener('click', function (e) {
      var target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  /* ────────────────────────────────────────────────────────────────
   * 1. GA4 dataLayer helper
   * ──────────────────────────────────────────────────────────────── */
  function gaPush(eventName, ecommerce, source) {
    var dl = (window.dataLayer = window.dataLayer || []);
    dl.push({ ecommerce: null }); // clear prior ecommerce object (GTM hygiene)
    dl.push({
      event: eventName,
      analyzify_source: source || 'product-drop',
      ecommerce: ecommerce,
    });
  }

  /* Tall-surface-safe IntersectionObserver, per §4d of the GA4 handoff spec:
   * fires when EITHER 50% of the element is visible OR the element covers
   * 50% of the viewport (long grids/sections never reach 0.5 ratio otherwise). */
  function observeOnce(el, key, seenSet, cb) {
    if (!el || seenSet.has(key)) return;
    if (!('IntersectionObserver' in window)) {
      // No IO support: fail safe by treating as visible immediately.
      seenSet.add(key);
      cb();
      return;
    }
    var thresh = [];
    for (var i = 0; i <= 20; i++) thresh.push(i / 20);

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (seenSet.has(key)) return;
        var vp = window.innerHeight || document.documentElement.clientHeight || 0;
        var coversHalfViewport = entry.intersectionRect.height >= vp * 0.5;
        if (entry.intersectionRatio < 0.5 && !coversHalfViewport) return;
        seenSet.add(key);
        cb();
        io.disconnect();
      });
    }, { threshold: thresh });

    io.observe(el);
  }

  document.querySelectorAll('.yfd-section').forEach(function (section) {
    var sectionId = section.id || 'yfd-section';
    var gaRegion = section.getAttribute('data-ga-region') || 'AU';
    var seen = new Set();

    function itemId(productId, variantId) {
      return 'shopify_' + gaRegion + '_' + productId + '_' + variantId;
    }

    /* custom.js loads as a plain (non-deferred) <script> in <head>, so by the
     * time a shopper can click an Add to Cart button, _s1 is almost always
     * ready — but "almost always" is exactly the failure mode worth guarding
     * against: a bare `_s1.addToCartAndRefreshCart(...)` call with no
     * readiness check throws synchronously if _s1 genuinely isn't defined
     * yet, before the `result && typeof result.then` check ever runs. That
     * leaves a button stuck on "Adding…" forever with nothing in the cart
     * and no visible error — this is the most likely cause of "add to cart
     * isn't working" on the bundle button. Every _s1 call in this file
     * (bundle add-to-cart, per-tile quick-add) routes through this guard. */
    function whenS1Ready(methodName, cb, onTimeout) {
      function ready() {
        return window._s1 && typeof window._s1[methodName] === 'function';
      }
      if (ready()) { cb(window._s1); return; }
      var tries = 0;
      var iv = setInterval(function () {
        if (ready()) { clearInterval(iv); cb(window._s1); }
        else if (++tries > 40) {  // ~10s, then give up
          clearInterval(iv);
          console.warn('[Yacht Fleet Drop] _s1.' + methodName + ' never became available.');
          if (typeof onTimeout === 'function') onTimeout();
        }
      }, 250);
    }

    /* ── 2. Product list impressions + selects (Chapter 1 grid + Chapter 2 grid) ── */
    section.querySelectorAll('[data-ga-list-id]').forEach(function (listEl) {
      var listId = listEl.getAttribute('data-ga-list-id');
      var listName = listEl.getAttribute('data-ga-list-name') || listId;
      var cards = listEl.querySelectorAll('[data-ga-card]');

      observeOnce(listEl, sectionId + ':' + listId, seen, function () {
        var items = [];
        cards.forEach(function (card) {
          // Best Sellers renders the LARGER of its desktop/mobile counts and
          // hides the extras per breakpoint via CSS (see the bs_limit_desktop/
          // bs_limit_mobile media queries in yacht-fleet-drop.liquid) —
          // offsetParent is null for display:none elements, so this keeps the
          // impression payload matching what the shopper actually sees rather
          // than over-reporting cards hidden at the current breakpoint.
          if (card.offsetParent === null) return;
          items.push({
            item_id: card.getAttribute('data-ga-id'),
            item_name: card.getAttribute('data-ga-name'),
            price: parseFloat(card.getAttribute('data-ga-price')) || 0,
            index: parseInt(card.getAttribute('data-ga-index'), 10) || 0,
            item_list_id: listId,
            item_list_name: listName,
          });
        });
        if (!items.length) return;
        gaPush('ee_view_item_list', { item_list_id: listId, item_list_name: listName, items: items });
      });

      listEl.addEventListener('click', function (e) {
        var card = e.target.closest('[data-ga-card]');
        if (!card || !listEl.contains(card)) return;
        gaPush('ee_select_item', {
          item_list_id: listId,
          item_list_name: listName,
          items: [{
            item_id: card.getAttribute('data-ga-id'),
            item_name: card.getAttribute('data-ga-name'),
            price: parseFloat(card.getAttribute('data-ga-price')) || 0,
            index: parseInt(card.getAttribute('data-ga-index'), 10) || 0,
            item_list_id: listId,
            item_list_name: listName,
          }],
        });
        // No preventDefault(): GA4 tags use sendBeacon and survive navigation.
      });
    });

    /* ── 2b. Product card image galleries (swipe/scroll + dot nav) ──
     * Each card with more than one image gets a horizontally scrollable,
     * scroll-snapped strip of images plus clickable dot indicators. Dot
     * clicks stop propagation so browsing a card's photos doesn't also fire
     * ee_select_item / navigate — only clicking an actual image or the card
     * body (a real link) counts as a selection. */
    section.querySelectorAll('[data-yfd-gallery]').forEach(function (gallery) {
      var dotsWrap = gallery.parentElement.querySelector('[data-yfd-dots]');
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('button');
      if (!dots.length) return;

      function setActive(index) {
        dots.forEach(function (d, i) {
          if (i === index) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          gallery.scrollTo({ left: gallery.clientWidth * i, behavior: 'smooth' });
          setActive(i);
        });
      });

      var scrollTimeout;
      gallery.addEventListener('scroll', function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var slideWidth = gallery.clientWidth || 1;
          setActive(Math.round(gallery.scrollLeft / slideWidth));
        }, 80);
      }, { passive: true });
    });

    /* ── 2c. Per-tile quick-add (size selector + Add to Cart on each card) ──
     * Runs regardless of whether a Bundle Builder is present (placed before
     * its early-return below). Each widget's <select> options ARE variant
     * IDs directly (rendered server-side per product), so no client-side
     * variant lookup/JSON is needed here — just read the select's value. */
    section.querySelectorAll('[data-yfd-quick-add]').forEach(function (widget) {
      var select = widget.querySelector('[data-yfd-quick-size]');
      var btn = widget.querySelector('[data-yfd-quick-add-btn]');
      var quickStatusEl = widget.querySelector('[data-yfd-quick-status]');
      if (!select || !btn) return;

      // Picking a size or clicking Add to Cart is a stronger, distinct action
      // from "selecting" the product to view its page — stop it from also
      // bubbling into the list's delegated ee_select_item click handler.
      select.addEventListener('click', function (e) { e.stopPropagation(); });
      select.addEventListener('change', function (e) { e.stopPropagation(); });

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var variantId = select.value;
        if (!variantId) return;

        var original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        if (quickStatusEl) quickStatusEl.textContent = '';

        function onSuccess() {
          btn.textContent = '✓ Added';
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 1800);
        }

        function onError(err) {
          console.error('[Yacht Fleet Drop] Quick-add error:', err);
          btn.textContent = original;
          btn.disabled = false;
          if (quickStatusEl) quickStatusEl.textContent = 'Something went wrong — please try again.';
        }

        // NOTE: does NOT push add_to_cart to GA4 — Analyzify already emits it
        // from the real /cart/add.js call triggered by addToCartAndRefreshCart.
        whenS1Ready('addToCartAndRefreshCart', function (s1) {
          var items = [{ id: parseInt(variantId, 10), quantity: 1 }];
          var result = s1.addToCartAndRefreshCart(items);
          if (result && typeof result.then === 'function') {
            result.then(onSuccess).catch(onError);
          } else {
            onSuccess();
          }
        }, function () {
          onError(new Error('_s1.addToCartAndRefreshCart never became available'));
        });
      });
    });

    /* ── 3. Bundle builder ── */
    var bundleRoot = section.querySelector('.yfd-bundle[data-ga-promo-id]');
    if (!bundleRoot) return;

    var promoId = bundleRoot.getAttribute('data-ga-promo-id');
    var promoName = bundleRoot.getAttribute('data-ga-promo-name') || 'Bundle Builder';

    var dataScript = document.getElementById(bundleRoot.getAttribute('data-items-script'));
    var items = [];
    try {
      items = dataScript ? JSON.parse(dataScript.textContent) : [];
    } catch (err) {
      items = [];
    }

    var tiers = [];
    try {
      tiers = JSON.parse(bundleRoot.getAttribute('data-tiers') || '[]');
    } catch (err2) {
      tiers = [];
    }
    var currencySymbol = bundleRoot.getAttribute('data-currency-symbol') || '$';

    // Cart-linked tier progress: the checkout discount (Shopify Function) is a
    // percentage off the WHOLE cart once its subtotal crosses a spend tier, so
    // the progress bar must project cart subtotal + builder selection — the
    // builder selection alone under-reports a shopper who already has items in
    // cart. SSR seeds the value (cents); cart:change keeps it fresh.
    var cartSubtotal = (parseInt(bundleRoot.getAttribute('data-cart-subtotal'), 10) || 0) / 100;

    document.addEventListener('cart:change', function (e) {
      var cart = e.detail && e.detail.cart;
      if (cart && typeof (cart.original_total_price || cart.total_price) === 'number') {
        cartSubtotal = (cart.original_total_price || cart.total_price) / 100;
        recalc();
        return;
      }
      // detail-less broadcast (defensive): fall back to fetching the cart
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        cartSubtotal = (c.original_total_price || c.total_price || 0) / 100;
        recalc();
      }).catch(function () { /* keep last known subtotal */ });
    });

    var tabButtons = bundleRoot.querySelectorAll('[data-yfd-tab]');
    var itemRows = bundleRoot.querySelectorAll('[data-yfd-item]');
    var fillEl = bundleRoot.querySelector('[data-yfd-tier-fill]');
    var markerEls = bundleRoot.querySelectorAll('[data-yfd-tier-marker]');
    var labelEls = bundleRoot.querySelectorAll('[data-yfd-tier-label]');
    var subtotalEl = bundleRoot.querySelector('[data-yfd-subtotal]');
    var finalEl = bundleRoot.querySelector('[data-yfd-final]');
    var saveMsgEl = bundleRoot.querySelector('[data-yfd-save-msg]');
    var addBtn = bundleRoot.querySelector('[data-yfd-add]');
    var statusEl = bundleRoot.querySelector('[data-yfd-status]');

    // Tab ids are generic ('a' / 'b' / 'custom') — they map to whichever two
    // buckets the merchant assigned via each product block's "Group" setting
    // (Group A / Group B), not to any hardcoded product category.
    var activeTab = 'a';

    // Liquid emits "key" as a bare JSON number ({{ product.id | json }}), but
    // row.getAttribute() always returns a string — a strict `===` between the
    // two silently never matches, which is what made every preset tab (and
    // the selected-items/subtotal calc) come up empty except "Custom Mix"
    // (whose branch doesn't depend on finding a match). Compare as strings.
    function findItemByKey(key) {
      var keyStr = String(key);
      return items.filter(function (i) { return String(i.key) === keyStr; })[0];
    }

    // The bundle tab buttons' visible text IS the merchant-configured label
    // (bundle_tab_a_label / bundle_tab_b_label / bundle_tab_custom_label
    // settings) — reading it back rather than hardcoding a string here means
    // the GA4 creative_name always matches what the shopper actually saw.
    function getTabLabel(tab) {
      var btn = bundleRoot.querySelector('[data-yfd-tab="' + tab + '"]');
      return btn ? btn.textContent.trim() : 'Bundle';
    }

    function applyPreset(tab) {
      activeTab = tab;
      tabButtons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-yfd-tab') === tab ? 'true' : 'false');
      });
      itemRows.forEach(function (row) {
        var key = row.getAttribute('data-yfd-item');
        var it = findItemByKey(key);
        var checkbox = row.querySelector('input[type="checkbox"]');

        // "custom" shows every item so the shopper can mix and match; the two
        // presets each show ONLY items in the matching group — this is what
        // actually filters the grid (previously this only (un)checked items,
        // leaving the other group visible — and checkable — under either tab).
        var matchesTab = tab === 'custom' || (tab === 'a' && it && it.group === 'a') || (tab === 'b' && it && it.group === 'b');
        row.hidden = !matchesTab;

        if (checkbox && tab !== 'custom') checkbox.checked = matchesTab;
        row.setAttribute('data-selected', checkbox && checkbox.checked ? 'true' : 'false');
      });
      recalc();
    }

    function selectedItems() {
      var out = [];
      itemRows.forEach(function (row) {
        var checkbox = row.querySelector('input[type="checkbox"]');
        if (!checkbox || !checkbox.checked) return;
        var key = row.getAttribute('data-yfd-item');
        var it = findItemByKey(key);
        if (it) out.push(it);
      });
      return out;
    }

    // Each item row owns its own size <select> (options are variant IDs
    // directly, same as the per-tile quick-add widgets) — this reads the
    // currently-chosen variant for one item by its key, straight from the DOM.
    function selectedVariantId(key) {
      var row = bundleRoot.querySelector('[data-yfd-item="' + key + '"]');
      var sel = row ? row.querySelector('[data-yfd-item-size]') : null;
      return sel ? sel.value : null;
    }

    function money(n) {
      return currencySymbol + n.toFixed(2);
    }

    function recalc() {
      var sel = selectedItems();
      var subtotal = sel.reduce(function (sum, it) { return sum + (it.price || 0); }, 0);

      // Tiers are judged on the PROJECTED cart (what's already in cart + this
      // selection) — the same number checkout's Function discount will see.
      var projected = cartSubtotal + subtotal;

      var reached = null;
      var next = null;
      for (var i = 0; i < tiers.length; i++) {
        if (projected >= tiers[i].threshold) reached = tiers[i];
        else { next = tiers[i]; break; }
      }

      var topThreshold = tiers.length ? tiers[tiers.length - 1].threshold : 1;
      var fillPct = Math.min(100, (projected / topThreshold) * 100);
      if (fillEl) fillEl.style.width = fillPct + '%';

      markerEls.forEach(function (m, i) {
        m.setAttribute('data-reached', tiers[i] && projected >= tiers[i].threshold ? 'true' : 'false');
      });
      labelEls.forEach(function (l, i) {
        l.setAttribute('data-reached', tiers[i] && projected >= tiers[i].threshold ? 'true' : 'false');
      });

      if (subtotalEl) subtotalEl.textContent = money(subtotal);

      // The struck price / final price stay scoped to THIS selection (that's
      // what the button adds); only the tier percentage comes from the
      // projected cart, mirroring how checkout discounts these items.
      if (reached && sel.length) {
        var discounted = subtotal * (1 - reached.percent / 100);
        if (finalEl) finalEl.textContent = money(discounted);
        if (subtotalEl) subtotalEl.classList.add('yfd-bundle__subtotal--struck');
        if (saveMsgEl) {
          saveMsgEl.classList.remove('yfd-bundle__save-msg--pending');
          saveMsgEl.textContent = reached.percent + '% OFF unlocked — you save ' + money(subtotal - discounted) + ' on these items!';
        }
      } else if (reached) {
        // Cart alone already crossed a tier, nothing selected here yet.
        if (finalEl) finalEl.textContent = money(subtotal);
        if (subtotalEl) subtotalEl.classList.remove('yfd-bundle__subtotal--struck');
        if (saveMsgEl) {
          saveMsgEl.classList.remove('yfd-bundle__save-msg--pending');
          saveMsgEl.textContent = reached.percent + '% OFF unlocked — anything you add gets ' + reached.percent + '% off too';
        }
      } else {
        if (finalEl) finalEl.textContent = money(subtotal);
        if (subtotalEl) subtotalEl.classList.remove('yfd-bundle__subtotal--struck');
        if (saveMsgEl) {
          saveMsgEl.classList.add('yfd-bundle__save-msg--pending');
          if (next) {
            var remaining = next.threshold - projected;
            saveMsgEl.textContent = 'Add ' + money(remaining) + ' more to unlock ' + next.percent + '% off';
          } else {
            saveMsgEl.textContent = 'Add items to unlock a bundle discount';
          }
        }
      }

      if (addBtn) addBtn.disabled = sel.length === 0;
    }

    itemRows.forEach(function (row) {
      var checkbox = row.querySelector('input[type="checkbox"]');
      if (!checkbox) return;
      row.addEventListener('click', function (e) {
        if (e.target === checkbox) return; // avoid double-toggle
        if (e.target.closest('select')) return; // picking a size shouldn't (un)check the item
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change'));
      });
      checkbox.addEventListener('change', function () {
        // Switching to Custom Mix unhides every item (it never touches checkbox
        // state when tab === 'custom'), so the item just toggled stays as-is.
        applyPreset('custom');
      });

      var itemSizeSelect = row.querySelector('[data-yfd-item-size]');
      if (itemSizeSelect) {
        itemSizeSelect.addEventListener('click', function (e) { e.stopPropagation(); });
        itemSizeSelect.addEventListener('change', function (e) { e.stopPropagation(); });
      }
    });

    tabButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyPreset(btn.getAttribute('data-yfd-tab'));
      });
    });

    // Impression: bundle builder is a *promotion* surface, not a list.
    observeOnce(bundleRoot, sectionId + ':bundle', seen, function () {
      var sel = selectedItems();
      var payloadItems = (sel.length ? sel : items).map(function (it, i) {
        var variantId = selectedVariantId(it.key) || (it.variants[0] || {}).id || '';
        return {
          item_id: itemId(it.productId, variantId),
          item_name: it.name,
          price: it.price,
          index: i + 1,
          promotion_id: promoId,
          promotion_name: promoName,
        };
      });
      gaPush('ee_view_promotion', {
        promotion_id: promoId,
        promotion_name: promoName,
        creative_name: getTabLabel(activeTab),
        creative_slot: 'bundle_builder',
        items: payloadItems,
      });
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var sel = selectedItems();
        if (!sel.length) return;

        // Each item's own dropdown only ever offers sizes that actually exist
        // (and are in stock) for that product, so there's nothing to resolve
        // or substitute here — just read what's selected.
        var lineItems = [];
        sel.forEach(function (it) {
          var variantId = selectedVariantId(it.key);
          if (!variantId) return;
          lineItems.push({ id: parseInt(variantId, 10), quantity: 1 });
        });

        if (!lineItems.length) {
          if (statusEl) {
            statusEl.textContent = 'Could not find available sizes for the selected items.';
            statusEl.className = 'yfd-bundle__status yfd-bundle__status--error';
          }
          return;
        }

        addBtn.disabled = true;
        if (statusEl) {
          statusEl.textContent = 'Adding bundle to cart…';
          statusEl.className = 'yfd-bundle__status';
        }

        function onSuccess() {
          addBtn.disabled = false;
          if (statusEl) {
            statusEl.textContent = 'Added ' + lineItems.length + ' item' + (lineItems.length === 1 ? '' : 's') + ' to your cart.';
            statusEl.className = 'yfd-bundle__status yfd-bundle__status--ok';
          }

          // GA4: this is the promotional "select" action — the customer committing
          // to the bundle offer. Do NOT push add_to_cart here; Analyzify already
          // emits product_added_to_cart -> ee_add_to_cart -> add_to_cart from the
          // /cart/add.js call triggered by _s1.addToCartAndRefreshCart above.
          gaPush('ee_select_promotion', {
            promotion_id: promoId,
            promotion_name: promoName,
            creative_name: getTabLabel(activeTab),
            creative_slot: 'bundle_builder',
            items: sel.map(function (it, i) {
              return {
                item_id: itemId(it.productId, selectedVariantId(it.key) || ''),
                item_name: it.name,
                price: it.price,
                index: i + 1,
                promotion_id: promoId,
                promotion_name: promoName,
              };
            }),
          });
        }

        function onError() {
          addBtn.disabled = false;
          if (statusEl) {
            statusEl.textContent = 'Something went wrong adding the bundle to your cart — please try again.';
            statusEl.className = 'yfd-bundle__status yfd-bundle__status--error';
          }
        }

        whenS1Ready('addToCartAndRefreshCart', function (s1) {
          var result = s1.addToCartAndRefreshCart(lineItems);
          if (result && typeof result.then === 'function') {
            result.then(onSuccess).catch(onError);
          } else {
            onSuccess();
          }
        }, function () {
          onError(new Error('_s1.addToCartAndRefreshCart never became available'));
        });
      });
    }

    applyPreset(activeTab);
  });
})();
